import { Badge, ButtonStyled } from "components/buttons/buttons.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { CustomLink, Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontSize } from "config/font";
import { CompanyRole } from "model/User";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import { useAuthStore } from "stores/authStore/authStore";
import { useChatStore } from "web-apps/company/stores/chatStore/chatStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import Logo from "../../../images/logo/svg/simflexity-logo.svg";
import { ChatContainerList } from "../chat/chatContainerList.component";
import { MenuItems } from "../menuList/menuItems.component";
import { NavBarMenuListStyled } from "../menuList/navBarMenuList.styled";
import {
  Burger,
  ChatButton,
  ChatIconWrapper,
  Flex1,
  LogInWrapper,
  NavBarStyled,
} from "./navBar.styled";
import { ProfileSelect } from "./profileSelect.component";

import { Modal } from "@material-ui/core";
import { PromptModal } from "components/modal/promptModal.component";
import { GigFormModal } from "web-apps/company/components/gig/gigForm/gigFormModal.component";
import { useMenuList } from "../menu-utils";

export type MenuItem = {
  text: string;
  path: string;
  image: string;
};

type Props = {
  menuList?: MenuItem[];
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const NavBar: React.FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();
  const [authState, authActions] = useAuthStore();
  const [chatState, chatDispatch] = useChatStore();

  const [companyState, , companyDispatch] = useCompanyStore();
  const [missedMessagesQty, setMissedMessagesQty] = useState(0);
  const [showChatContainer, setShowChatContainer] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const userType =
    authState.accountType === "Company" &&
    authState.user?.role === CompanyRole.COMPANY_TIMESTAMP
      ? "CompanyTimeStamp"
      : authState.accountType;

  const menuList = useMenuList(userType, t);

  const haveChatModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: any) => s.key === "CompanyShowChatModule"
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  const handleClick = async () => {
    authActions.signOut().then(() => {
      companyDispatch({ type: "CLEAR_COMPANY_INFORMATION" });
      chatDispatch({ type: "CLEAR_CHAT_DATA" });
      history.push("/company/login");
      setOpen(!open);
    });
  };

  useEffect(() => {
    const unreadMessagesCount = chatState.state.summary.chats.reduce(
      (previousValue, { unread }) => previousValue + unread,
      0
    );
    setMissedMessagesQty(unreadMessagesCount);
  }, [chatState.state.summary]);

  return (
    <>
      <NavBarStyled>
        {authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP && (
          <Link to="/company/calendar">
            <img src={Logo} alt="Logo" style={{ height: 50 }} />
          </Link>
        )}
        {authState.user?.role == CompanyRole.COMPANY_TIMESTAMP && (
          <Link to="/company/stampclock">
            <img src={Logo} alt="Logo" style={{ height: 50 }} />
          </Link>
        )}
        <Flex1>
          <NavBarMenuListStyled>
            {companyState.company && (
              <MenuItems menuList={menuList} open={open} setOpen={setOpen} />
            )}
          </NavBarMenuListStyled>
          {authState.signedIn &&
            companyState.company &&
            authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP &&
            haveChatModule && (
              <ChatButton
                onMouseEnter={() => {
                  setShowChatContainer(true);
                }}
                onMouseLeave={() => {
                  setShowChatContainer(false);
                }}
              >
                <ChatIconWrapper>
                  <CustomIcon
                    name="chat"
                    color={Color.SeaBlue500}
                    padding="5px"
                  />
                  {missedMessagesQty > 0 && (
                    <Badge>
                      <Text fontSize={FontSize.Small} color={Color.White}>
                        {missedMessagesQty}
                      </Text>
                    </Badge>
                  )}
                </ChatIconWrapper>
                {showChatContainer && (
                  <ChatContainerList
                    setShowChatContainer={setShowChatContainer}
                  />
                )}
              </ChatButton>
            )}
          {authState.signedIn &&
            companyState.company &&
            authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP && (
              <ButtonStyled
                onClick={() => setShowCreateModal(true)}
                backgroundColor={Color.ModernGreen}
                style={{ marginLeft: 10, height: "40px", marginTop: 20 }}
              >
                {t("GigsCompany.CreateGig")}
              </ButtonStyled>
            )}
        </Flex1>
        <LogInWrapper>
          {authState.signedIn &&
          companyState.company &&
          authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP ? (
            <ProfileSelect open={open} setOpen={setOpen} />
          ) : authState.signedIn ? (
            <CustomLink onClick={handleClick} color={Color.Destructive}>
              {t("ProfileCompany.SignOut")}
            </CustomLink>
          ) : (
            <>
              <CustomLink to={`${path}/login`} color={Color.MidnightBlue}>
                {t("LoginCompany.CompanyLogin")}
              </CustomLink>
            </>
          )}
        </LogInWrapper>
        <Burger>
          <CustomIcon
            name="hamburger"
            size="40px"
            onClick={() => {
              setOpen(!open);
            }}
          />
        </Burger>
      </NavBarStyled>
      <GigFormModal
        showGigFormModal={showCreateModal}
        setShowGigFormModal={setShowCreateModal}
      />
      <Modal
        open={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
        }}
      >
        <PromptModal
          header={t("GigsCompany.AllYourDataWillBeLost")}
          message={t("GigsCompany.DoYouWishToContinueAnyway")}
          accept={{
            label: t("General.Continue"),
            color: Color.MidnightBlue,
            callback: () => {
              setShowCreateModal(false);
              setShowWarningModal(false);
            },
          }}
          cancel={{
            label: t("General.Cancel"),
            color: Color.Destructive,
            callback: () => {
              setShowWarningModal(false);
            },
          }}
        />
      </Modal>
    </>
  );
};
